'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { Button, Icon } from '~/components/primitives';
import { useI18n } from '~/features/i18n';
import { usePostHog } from 'posthog-js/react';
import { usePortalCtx, usePortalRouter } from '~/features/global/portal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '~/components/ui/dialog';
import useWishlistState from './useWishlistState';
import { createSerializer } from 'nuqs';
import { Wishlist_SharedPayload } from '../cart/wishlist/wishlist.shared';
import { useQuery } from '@tanstack/react-query';
import { Atom, useAtom, useAtomSet, useAtomValue } from '@effect-atom/atom-react';
import { PORTAL_RELATIVE_HREF_WISHLIST } from '../cart/hooks';
import { CACHED_PARTICIPANTS_FILTER } from '../activities/search/client/context';
import { useClipboard_CopySDK } from '~/modules/browser/clipboard/components';
const isOpenAtom = Atom.make(false);
const SERIALIZER = createSerializer({
  [Wishlist_SharedPayload.QUERY_PARAM_NAME]: Wishlist_SharedPayload.QUERY_PARAM_PARSER
});
export function useWishlist_ShareLinkDialog_OpenAction() {
  const $ = _c(2);
  const setIsOpen = useAtomSet(isOpenAtom);
  let t0;
  if ($[0] !== setIsOpen) {
    t0 = () => setIsOpen(true);
    $[0] = setIsOpen;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export const Wishlist_ShareLinkDialog_Mount: React.FC<{
  participants?: number;
}> = props => {
  const $ = _c(4);
  const isOpen = useAtomValue(isOpenAtom);
  if (!isOpen) {
    return null;
  }
  let t0;
  if ($[0] !== props.participants) {
    t0 = props.participants ?? CACHED_PARTICIPANTS_FILTER.getValue();
    $[0] = props.participants;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <Wishlist_ShareLinkDialog participants={t0} />;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
const Wishlist_ShareLinkDialog: React.FC<{
  participants: number;
}> = props => {
  const i18n = useI18n();
  const posthog = usePostHog();
  const portal = usePortalCtx();
  const router = usePortalRouter();
  const wishlist = useWishlistState();
  const [isOpen, setIsOpen] = useAtom(isOpenAtom);
  const windowOrigin = useQuery({
    queryKey: ['window.location.origin'],
    queryFn() {
      return window.location.origin;
    }
  });
  const url_0 = useMemo(() => {
    if (!windowOrigin.data) return null;
    const url = new URL(router.relativePath(PORTAL_RELATIVE_HREF_WISHLIST), windowOrigin.data);
    url.search = SERIALIZER({
      shared: {
        items: wishlist.items,
        participants: props.participants
      }
    });
    return url.toString();
  }, [windowOrigin.data, wishlist.items, props.participants]);
  const copySDK = useClipboard_CopySDK(() => url_0 ?? '', {
    onCopy() {
      posthog.capture('wishlist.shared', {
        portal: portal.portal,
        item_count: wishlist.items.length
      });
    }
  });
  if (!url_0) return null;
  return <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className="sm:max-w-lg" data-testid="share-wishlist-dialog">
                <DialogHeader>
                    <DialogTitle>
                        {i18n._('wishlist.share.dialog.title', undefined, {
            message: 'Share your wishlist'
          })}
                    </DialogTitle>
                    <DialogDescription>
                        {i18n._('wishlist.share.dialog.description_{count}', {
            count: wishlist.items.length
          }, {
            message: 'Share this link with colleagues so they can import your {count} wishlist activities. They can choose to add them to their own wishlist or replace it entirely.'
          })}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2">
                    <div className="flex gap-2">
                        <input type="text" readOnly value={url_0} className="flex-1 rounded-lg border border-blue-200 bg-blue-50/50 px-3 py-2 text-xs text-primary outline-none focus:border-blue-400" onFocus={e => e.target.select()} />
                        <Button variant="tertiary" size="sm" iconBefore={copySDK.isCopied ? 'check' : 'copy'} onClick={copySDK.onCopy}>
                            {copySDK.isCopied ? i18n._('wishlist.share.dialog.copied', undefined, {
              message: 'Copied!'
            }) : i18n._('wishlist.share.dialog.copy', undefined, {
              message: 'Copy link'
            })}
                        </Button>
                    </div>
                    {copySDK.isCopied && <p className="flex items-center gap-1.5 text-xs font-medium text-green-600">
                            <Icon name="check" size="xs" />
                            {i18n._('wishlist.share.dialog.copied-confirmation', undefined, {
            message: 'Link copied to clipboard!'
          })}
                        </p>}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setIsOpen(false)}>
                        {i18n._('Close')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>;
};